import { getGroup } from "routes/schedule/controller/get-group"
import { saveGroupDataByIds } from "routes/schedule/controller/store-group-data"
import { setGroupPayloadUsingIds } from "routes/schedule/controller/set-group-payload-using-ids"

/**
 * Save a regime-tailoring payload for a given schedule, handling both normal and global tailoring.
 * Status persistence is best-effort: if the status write fails after a successful payload save,
 * the error is logged but not propagated (the payload write already succeeded against a now-
 * incremented tree version, so retrying would cause a spurious conflict).
 *
 * @param {object} payload - The tailoring payload to save
 * @param {object} ctx - Save context
 * @param {string} ctx.treeId
 * @param {string} ctx.groupId
 * @param {string} ctx.scheduleId
 * @param {string} ctx.scheduleRecordId
 * @param {string} ctx.groupAndTreeId
 * @param {string} ctx.id
 * @param {boolean} ctx.globalTailoring
 * @param {number} [ctx.expectedTreeVersion]
 * @param {object} [options]
 * @param {boolean} [options.forceOverwrite]
 * @returns {Promise<object>} Server result (may include conflict/treeVersion)
 */
export async function saveRegimeTailoringPayload(payload, ctx, options = {}) {
    const opts = {
        ...(ctx.expectedTreeVersion != null ? { expectedTreeVersion: ctx.expectedTreeVersion } : {}),
        ...(options.forceOverwrite ? { forceOverwrite: true } : {}),
    }
    if (!ctx.globalTailoring) {
        const result = await setGroupPayloadUsingIds(ctx.treeId, ctx.groupId, ctx.scheduleId, payload, opts)
        if (result && !result.conflict && !result.stale && !result.skipped && result.success !== false) {
            try {
                const record = await getGroup(ctx.groupAndTreeId)
                if (record?.data) {
                    const statuses = (record.data.statuses ??= {})
                    statuses[ctx.scheduleRecordId] = payload.status
                    await saveGroupDataByIds(ctx.treeId, ctx.groupId, record.data, undefined)
                }
            } catch (e) {
                console.warn("Status persistence failed after successful payload save:", e?.message || e)
            }
        }
        return result
    } else {
        return setGroupPayloadUsingIds(ctx.treeId, ctx.groupId, ctx.id, payload, opts)
    }
}
